import { type Clarification, useClarificationWrites, useClarifications } from '@/api/clarifications';
/**
 * Kontroll: Gjennomgang, Registreringer and Per ansatt (V7.5.6, #275; plan 0026 "Timeliste as the
 * month-close workbench"). Heading, three local tabs, one scope toolbar (period, team, person; week and
 * search under «Flere filtre», one disclosure on a phone), the overall band, then one working list: the
 * records (timeliste/KontrollQueue.tsx) or the employees (KontrollPeople.tsx). timeliste/kontroll.ts decides
 * what every shift is, on close.ts's kind and review.ts's overlap and invalid time, so the band, the list,
 * the exception filter, Per ansatt and approve-and-next agree. The record panel is KontrollRecord.tsx, the
 * batch confirmation and «Legg til registrering» KontrollDialogs.tsx. The measured comparison with the
 * prototype that drove this layout is DIFF D001 to D181 in the #275 work notes.
 *
 * The band follows the scope only. Correcting (inline, PUT /admin/shifts/:id) and approving
 * (PUT /admin/shifts/:id/accept, one call per shift) stay separate. A batch approves only ticked ready
 * records after the confirmation. «Godkjenn og neste» walks the queue captured when the panel opened.
 * «Be om avklaring» (api/clarifications.ts) holds a record's approval on the server.
 *
 * Addresses: ?mnd=YYYY-MM | ?uke=YYYY-MM-DD (period), ?team=, ?ansatt=7 or 7,8,9, ?sok=, ?prosjekt=,
 * ?fane=registreringer|ansatte (Gjennomgang without), ?vis=handling|klare|godkjent|alle|gjenstar|pagar,
 * ?avvik=<exception>, ?sort=prioritet|dato|ansatt, ?side=N, ?astatus=, ?asort=, ?aside=, ?fra=ansatte
 * (drilled from Per ansatt), ?vakt=<id>, ?rett=inn|ut|begge, ?ny=registrering, ?fra=lonn, ?tilbake=<path>,
 * ?arbeidsliste= (Oversikt's lists), ?poster=1,2,3&fra=saker (a Saker worklist: exactly those records, with Saker
 * as the origin and ?tilbake= back to the same task, #286). Old /arbeidstid list addresses land here (Arbeidstid.tsx
 * kontrollAddress).
 */
import { errorText, put } from '@/api/client';
import {
  type ApiShift,
  useAbsences,
  useShiftTypes,
  useShiftWrites,
  useShifts,
  useUsers,
  useWorkTimeChanges,
} from '@/api/data';
import { projectLabel, useProjectWord, useProjects, useProjectsOn } from '@/api/projects';
import { invalidateShifts, revisionOf, useReportReceipt } from '@/api/shifts';
import { useCan } from '@/auth/caps';
import { useAuth } from '@/auth/store';
import { T, useT } from '@/i18n';
import { type AbsenceDraft, AbsenceDrawer, newAbsenceDraft } from '@/pages/fravaer/AbsenceDrawer';
import {
  breakPolicy,
  dayEntries,
  rulePerson,
  ruleText,
  tenantRules,
  today,
  worked,
  workedResult,
} from '@/rules';
import { askBeforeLeaving } from '@/shell/context';
import { useTenant } from '@/shell/tenant';
import { Button, Skeleton, toast } from '@/ui';
import { PeriodControl } from '@/ui/PeriodControl';
import { fullName, monthLabel, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Notice, TabPanel } from '@/ui/page';
import { useQueryClient } from '@tanstack/react-query';
import { addDays, mondayOf } from '@timefly/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { safeReturn } from './arbeidstid/period';
import { recordCsv } from './arbeidstid/record';
import { AddDialog, BatchDialog, ReportDialog } from './timeliste/KontrollDialogs';
import { type Drill, KontrollPeople, type PeopleFilter } from './timeliste/KontrollPeople';
import { KontrollQueue, PAGE } from './timeliste/KontrollQueue';
import { KontrollRecord } from './timeliste/KontrollRecord';
import { KontrollSummary } from './timeliste/KontrollSummary';
import { TimerHead } from './timeliste/TimerHead';
import { Waiting } from './timeliste/Waiting';
import { useApproveMany } from './timeliste/approve';
import { dateShort, osloDay } from './timeliste/kfmt';
import {
  EXCEPTIONS,
  EXCEPTION_TEXT,
  type Rec,
  absentOn,
  bySeverity,
  classify,
  exceptionOf,
  matches,
  nextIn,
  perPerson,
  sortPeople,
  summarize,
} from './timeliste/kontroll';
import { blocksOf } from './timeliste/review';
import './timeliste/kontroll.css';
import { WORKLISTS, inWorklist, worklistOf } from './timeliste/worklist';
import { type DraftShift, ShiftDrawer, draftOf } from './vaktplan/ShiftDrawer';

const dateOf = (s: ApiShift) => osloDay(s.start_time);
const regs = (n: number) => T('{n, plural, one {# registrering} other {# registreringer}}', { n });

type Filter = 'handling' | 'klare' | 'gjenstar' | 'godkjent' | 'alle' | 'pagar' | 'mangler' | 'uten';
const FILTERS: Filter[] = ['handling', 'klare', 'gjenstar', 'godkjent', 'alle', 'pagar', 'mangler', 'uten'];
/** the list headings, as the prototype's status labels (D052) */
const FILTER_TEXT: Record<Filter, string> = {
  handling: 'Trenger vurdering',
  klare: 'Klare til godkjenning',
  gjenstar: 'Gjenstående registreringer',
  godkjent: 'Godkjente registreringer',
  alle: 'Alle registreringer',
  pagar: 'Pågående registreringer',
  mangler: 'Planlagte vakter uten registrering',
  uten: 'Mangler stempling',
};
const inFilter = (f: Filter, r: Rec): boolean =>
  f === 'handling'
    ? r.bucket === 'review'
    : f === 'klare'
      ? r.bucket === 'ready'
      : f === 'gjenstar'
        ? r.bucket === 'review' || r.bucket === 'ready'
        : f === 'godkjent'
          ? r.bucket === 'approved'
          : f === 'pagar'
            ? r.bucket === 'ongoing'
            : // an expected shift with no registration is its own list, not a record (AC-13)
              f === 'mangler'
              ? r.bucket === 'gap'
              : // the old «uten stempling» links (Oversikt, Lønn): no clocking or no clock-out
                f === 'uten'
                ? r.bucket === 'gap' || (r.bucket === 'review' && r.exceptions.includes('missing'))
                : // every ended record, gaps too: Oversikt's and Saker's exact lists open here
                  r.bucket !== 'planned';
type Sort = 'prioritet' | 'dato' | 'ansatt';

const monthOf = (d: string) => d.slice(0, 7);
const firstOf = (m: string) => `${m}-01`;
const lastOf = (m: string) => {
  const [y, mo] = m.split('-').map(Number) as [number, number];
  return `${m}-${String(new Date(Date.UTC(y, mo, 0)).getUTCDate()).padStart(2, '0')}`;
};
const stepMonth = (m: string, by: number) => {
  const [y, mo] = m.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, mo - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const pageOf = (v: string | null) => Math.max(0, (Number(v) || 1) - 1);
/** the Mondays of a month's weeks, and a week as «28. sep. – 4. okt.» (D029) */
const weeksOf = (m: string) => {
  const out: string[] = [];
  for (let d = firstOf(m); d <= lastOf(m); d = addDays(d, 1))
    if (!out.includes(mondayOf(d))) out.push(mondayOf(d));
  return out;
};
const weekText = (w: string) => `${dateShort(w)} – ${dateShort(addDays(w, 6))}`;

export default function Timeliste() {
  const t = useT();
  const can = useCan();
  const mayApprove = can('timeliste.godkjenne');
  const mayEdit = can('timeliste.rette');
  const me = useAuth((a) => a.user?.id ?? null);
  const { tenant, workspace } = useTenant();
  const [sp, setSp] = useSearchParams();
  /** a filter change replaces the address; a tab, a summary, a drill, a page or a period is a step Back undoes */
  const edit = (f: (n: URLSearchParams) => void, push = false) => {
    const n = new URLSearchParams(sp);
    f(n);
    if (['mnd', 'uke'].some((key) => n.get(key) !== sp.get(key))) {
      // another period is not the worklist Saker sent: its exact records go with it (#286)
      for (const k of ['arbeidsliste', 'valgt', 'vakt', 'side', 'aside', 'poster']) n.delete(k);
    }
    setSp(n, { replace: !push });
  };
  const askedWeek = /^\d{4}-\d{2}-\d{2}$/.test(sp.get('uke') ?? '')
    ? mondayOf(sp.get('uke') as string)
    : null;
  const askedMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(sp.get('mnd') ?? '') ? (sp.get('mnd') as string) : null;
  const badMonth = !!sp.get('mnd') && !askedMonth;
  const span: 'maned' | 'uke' = askedWeek ? 'uke' : 'maned';
  // a week belongs to the month it was chosen in (the week select lists that month's weeks), else its Thursday's
  const month = askedMonth ?? monthOf(askedWeek ? addDays(askedWeek, 3) : today());
  const from = askedWeek ?? firstOf(month);
  const to = askedWeek ? addDays(askedWeek, 6) : lastOf(month);
  const fane = sp.get('fane');
  const view: 'gjennomgang' | 'registreringer' | 'ansatte' =
    fane === 'ansatte' ? 'ansatte' : fane === 'registreringer' ? 'registreringer' : 'gjennomgang';
  // from the Monday of the first week: the §10-7 record counts a running week from its start (record.ts)
  const fetchFrom = mondayOf(from) < addDays(from, -1) ? mondayOf(from) : addDays(from, -1);
  const shiftsQ = useShifts(fetchFrom, addDays(to, 1));
  const usersQ = useUsers();
  const typesQ = useShiftTypes();
  const clarQ = useClarifications(from, to);
  const clarW = useClarificationWrites();
  // approved absence accounts for a planned shift with no clocking (AC-12, AC-13); a reader without fravaer.se
  // does not get the list, and such a shift then stays a gap
  const absQ = useAbsences();
  const absences = useMemo(() => (Array.isArray(absQ.data) ? absQ.data : []), [absQ.data]);
  // corrected records read «Rettet, klar» (D080): the period's «changed» history rows
  const changesQ = useWorkTimeChanges({ from, to, action: 'changed' });
  const projectsOn = useProjectsOn();
  const projects = useProjects().data ?? [];
  const projectWord = useProjectWord();
  const prosjekt = projectsOn ? sp.get('prosjekt') : null;
  const w = useShiftWrites();
  const qc = useQueryClient();
  const rules = tenantRules();
  const policy = breakPolicy(tenant);
  const tol = rules.toleranceMin ?? 15;
  const now = Date.now();
  // a refresh that failed over data already shown: writes wait for a fresh read (D166)
  const stale = shiftsQ.isError && !!shiftsQ.data;
  const writable = !stale && !shiftsQ.isPending;

  const selectedId = sp.get('vakt') ?? sp.get('valgt');
  const openId = /^\d+$/.test(selectedId ?? '') ? Number(selectedId) : null;
  const setOpenId = (id: number | null) =>
    edit((n) => {
      n.delete('valgt');
      if (id == null) n.delete('vakt');
      else n.set('vakt', String(id));
    });
  const [drawer, setDrawer] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [absDraft, setAbsDraft] = useState<AbsenceDraft | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [queue, setQueue] = useState<number[] | null>(null);
  const [batch, setBatch] = useState<number[] | null>(null);
  const [report, setReport] = useState<ApiShift[] | null>(null);
  const receipt = useReportReceipt();
  const scope = (sp.get('ansatt') ?? '')
    .split(',')
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0);
  const who: number | 'alle' = scope.length === 1 ? (scope[0] as number) : 'alle';
  const teamAsked = sp.get('team') || 'alle';
  const needle = sp.get('sok') ?? '';
  const [more, setMore] = useState(false);
  const [typed, setTyped] = useState(needle);
  useEffect(() => setTyped(needle), [needle]);

  const personOf = useMemo(() => new Map((usersQ.data ?? []).map((u) => [u.id, u])), [usersQ.data]);
  const shifts = useMemo(
    () =>
      (shiftsQ.data?.shifts ?? []).filter((s) => {
        const d = dateOf(s);
        return s.user_id != null && d >= from && d <= to;
      }),
    [shiftsQ.data, from, to],
  );
  const userOfShift = useMemo(() => new Map(shifts.map((s) => [s.user_id as number, s.user])), [shifts]);
  const nameById = (id: number | null) =>
    id == null ? T('ukjent') : fullName(personOf.get(id) ?? userOfShift.get(id) ?? { name: `#${id}` });
  const nameOf = (s: ApiShift) => fullName(s.user ?? personOf.get(s.user_id ?? -1) ?? { name: '' });
  const teamsOf = (uid: number | null) =>
    (personOf.get(uid ?? -1)?.teams ?? []).filter((x) => {
      const tid = (x as { tenant_id?: number }).tenant_id;
      return tid == null || tid === tenant?.id;
    });
  /** «Kjøkken, 104»: the team and the employee number (D066; no middle dot) */
  const subOf = (uid: number | null) =>
    [
      teamsOf(uid)
        .map((x) => x.name)
        .join(', '),
      personOf.get(uid ?? -1)?.employee_id ?? '',
    ]
      .filter(Boolean)
      .join(', ');

  const clars = useMemo(() => (Array.isArray(clarQ.data) ? clarQ.data : []), [clarQ.data]);
  const clarOf = (id: number): Clarification | undefined =>
    clars.find((c) => c.shift_id === id && c.status === 'open') ??
    clars.find((c) => c.shift_id === id && c.status === 'answered');
  const waiting = useMemo(
    () => new Set(clars.filter((c) => c.status === 'open').map((c) => c.shift_id)),
    [clars],
  );
  const corrected = useMemo(
    () =>
      new Set(
        (Array.isArray(changesQ.data) ? changesQ.data : [])
          .map((c) => c.shift_id)
          .filter((x): x is number => x != null),
      ),
    [changesQ.data],
  );
  const blocks = useMemo(() => blocksOf(shifts), [shifts]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: now and the fixed rules are read once per data change, not per render
  const recs = useMemo(() => {
    const ctx = {
      now,
      tol,
      blocks,
      waiting,
      corrected,
      long: (s: ApiShift) => {
        const p = personOf.get(s.user_id ?? -1);
        return !!p && dayEntries(s, rulePerson(p, tenant), rules).some((e) => e.code === 'day_over_13');
      },
      pauseCovers: (s: ApiShift) => {
        if (
          !s.punch_in_time ||
          !s.punch_out_time ||
          Date.parse(s.punch_out_time) <= Date.parse(s.punch_in_time)
        )
          return false;
        const r = workedResult(s, policy);
        return r.status === 'ok' && r.minutes === 0;
      },
      unplanned: (s: ApiShift) => /uten plan/i.test(s.shiftType?.name ?? ''),
      absent: (s: ApiShift) => absentOn(absences, s),
    };
    return new Map(shifts.map((s) => [s.id, classify(s, ctx)]));
  }, [shifts, blocks, waiting, corrected, personOf, policy, tol, tenant, absences]);
  const recOf = (s: ApiShift) => recs.get(s.id) as Rec;

  // the team list and the person list: every active person, narrowed by the team (D032)
  const allTeams = [...new Set((usersQ.data ?? []).flatMap((u) => teamsOf(u.id).map((x) => x.name)))].sort(
    (a, b) => a.localeCompare(b, 'nb'),
  );
  const team =
    teamAsked !== 'alle' && allTeams.length > 0 && !allTeams.includes(teamAsked) ? 'alle' : teamAsked;
  const people = (usersQ.data ?? [])
    .filter(
      (u) =>
        (u.active_status || shifts.some((s) => s.user_id === u.id)) &&
        (team === 'alle' || teamsOf(u.id).some((x) => x.name === team)),
    )
    .map((u) => ({ id: u.id, name: fullName(u) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'nb'));
  const q = needle.trim().toLocaleLowerCase('nb');
  // search matches the name and the employee number (D028)
  const scoped = shifts.filter(
    (s) =>
      (!scope.length || scope.includes(s.user_id as number)) &&
      (prosjekt == null ||
        (prosjekt === 'uten' ? s.project_id == null : s.project_id === Number(prosjekt))) &&
      (team === 'alle' || teamsOf(s.user_id).some((x) => x.name === team)) &&
      (!q ||
        `${nameOf(s)} ${personOf.get(s.user_id ?? -1)?.employee_id ?? ''}`
          .toLocaleLowerCase('nb')
          .includes(q)),
  );
  // a Saker worklist (#286): exactly the records it named, approved ones included so progress shows
  const poster = (sp.get('poster') ?? '')
    .split(',')
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0);
  const fromSaker = sp.get('fra') === 'saker' && poster.length > 0;
  // from a Lønnsgrunnlag check: back to that page's own address, only ever a /lonn one (#264)
  const payrollBack = (() => {
    const r = safeReturn(sp.get('tilbake'));
    return r && /^\/lonn(\?|$)/.test(r) ? r : null;
  })();
  const payrollBackMonth = payrollBack
    ? (new URLSearchParams(payrollBack.split('?')[1] ?? '')
        .get('mnd')
        ?.match(/^\d{4}-(0[1-9]|1[0-2])$/)?.[0] ?? null)
    : null;
  const posterSet = new Set(poster);
  const inPoster = fromSaker ? shifts.filter((s) => posterSet.has(s.id)) : [];
  const scopedRecs = (fromSaker ? scoped.filter((s) => posterSet.has(s.id)) : scoped).map(recOf);
  const hoursOf = (s: ApiShift) => worked(s, policy);
  const loading = shiftsQ.isPending || usersQ.isPending;
  const failed = shiftsQ.isError && !shiftsQ.data;
  const sum = loading || failed ? null : summarize(scopedRecs, hoursOf);

  const legacy = sp.get('vis');
  const exception = exceptionOf(sp.get('avvik') ?? (legacy === 'avvik' ? 'deviation' : null));
  const filter: Filter = ((): Filter => {
    if (fromSaker) return 'alle';
    const v = (legacy === 'avvik' ? 'handling' : legacy) as Filter | null;
    if (v && FILTERS.includes(v)) return v;
    return view === 'registreringer' ? 'alle' : 'handling';
  })();
  const sort: Sort = (['dato', 'ansatt'] as Sort[]).includes(sp.get('sort') as Sort)
    ? (sp.get('sort') as Sort)
    : 'prioritet';
  const page = pageOf(sp.get('side'));
  const setPage = (p: number) => {
    edit((n) => (p > 0 ? n.set('side', String(p + 1)) : n.delete('side')), true);
    requestAnimationFrame(() => document.getElementById('kqueue-h')?.focus());
  };
  const clearPicks = () => setPicked(new Set());
  /**
   * A summary figure or link: that status, the exception filter cleared, page 1. On Gjennomgang it stays on
   * Gjennomgang (D049); from Registreringer and Per ansatt it opens Registreringer with that status (D048).
   */
  const setFilter = (f: Filter, push = true) => {
    edit((n) => {
      for (const k of ['avvik', 'side', 'arbeidsliste', 'astatus', 'asort', 'aside']) n.delete(k);
      if (view !== 'gjennomgang') n.set('fane', 'registreringer');
      n.set('vis', f);
    }, push);
    clearPicks();
  };

  const { approve, busy, dialog: absenceDialog } = useApproveMany(shifts);
  const scopeText = [
    span === 'uke' ? weekText(from) : monthLabel(month),
    team === 'alle' ? T('Alle team') : team,
    who === 'alle' ? '' : nameById(who),
    q ? T('Søk: «{q}»', { q: needle.trim() }) : '',
  ]
    .filter(Boolean)
    .join(', ');

  function download(name: string, text: string) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  /** the records the scope on screen holds: the filters, or exactly a Saker worklist's (#295 C07: never the whole company) */
  const inScope = fromSaker ? scoped.filter((s) => posterSet.has(s.id)) : scoped;
  function exportCsv() {
    if (!policy) return toast(T('Bedriftens pauseinnstilling er ikke lastet ennå'));
    setReport(inScope.filter((s) => s.is_approved && s.user));
  }
  function csvOf(list: ApiShift[]) {
    const clock = (iso: string | null) =>
      iso
        ? new Date(iso).toLocaleTimeString('nb-NO', {
            timeZone: 'Europe/Oslo',
            hour: '2-digit',
            minute: '2-digit',
          })
        : '';
    const csv = [
      ['Ansatt', 'Dato', 'Inn', 'Ut', T('Timer etter pause (hele vakten)'), 'Vakttype'],
      ...list.map((s) => {
        const h = worked(s, policy);
        return [
          fullName(s.user ?? { name: '' }),
          dateOf(s),
          clock(s.punch_in_time),
          clock(s.punch_out_time),
          h == null ? '' : num(h),
          s.shiftType?.name ?? '',
        ];
      }),
    ]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    return `\uFEFF${csv}`;
  }
  /** the receipt first, then the download (#295 ONB-16): a refused receipt downloads nothing */
  async function makeReport(list: ApiShift[]) {
    const content = csvOf(list);
    try {
      await receipt.mutateAsync({ from, to, scope_label: scopeText, shifts: list, content });
    } catch (e) {
      setReport(null);
      invalidateShifts(qc);
      return toast(errorText(e));
    }
    setReport(null);
    download(span === 'uke' ? `timeliste-${from}-${to}.csv` : `timeliste-${month}.csv`, content);
    toast(T('Rapport klargjort. Nedlasting startet.'));
  }
  function exportRecord() {
    const ids = new Set(scoped.map((s) => s.user_id));
    const users = (usersQ.data ?? []).filter((u) => ids.has(u.id));
    download(`arbeidstid-${from}-${to}.csv`, recordCsv(users, shiftsQ.data?.shifts ?? [], from, to, policy));
  }

  const unplannedType = (typesQ.data ?? []).find((x) => /uten plan/i.test(x.name ?? '')) ?? typesQ.data?.[0];
  const askedNew = sp.get('ny') === 'registrering';
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when the types arrive
  useEffect(() => {
    if (!askedNew || !typesQ.data) return;
    if (mayEdit) setAdding(true);
    edit((n) => n.delete('ny'));
  }, [askedNew, typesQ.data]);
  // Kom i gang's «Bare rapport over godkjente timer» (#295): the preview once the records are read
  const askedReport = sp.get('rapport') === 'godkjente';
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when the records arrive
  useEffect(() => {
    if (!askedReport || !shiftsQ.data || !usersQ.data) return;
    exportCsv();
    edit((n) => n.delete('rapport'));
  }, [askedReport, shiftsQ.data, usersQ.data]);
  const projectName = (s: ApiShift) =>
    s.project_id == null
      ? projectWord.none
      : (projects.find((p) => p.id === s.project_id)?.name ?? `${projectWord.one} ${s.project_id}`);

  // ── the list ─────────────────────────────────────────────────────────────────────────────
  const worklist = worklistOf(sp.get('arbeidsliste'));
  const inStatus = scopedRecs.filter(
    (r) => inFilter(filter, r) && (!worklist || inWorklist(worklist, r.kind)),
  );
  const listRecs = inStatus
    .filter((r) => matches(r, exception))
    .sort((a, b) => {
      const byDate = a.s.start_time.localeCompare(b.s.start_time);
      const byName = nameOf(a.s).localeCompare(nameOf(b.s), 'nb');
      if (sort === 'dato') return byDate || byName;
      if (sort === 'ansatt') return byName || byDate;
      return bySeverity(a, b) || byName;
    });
  const listRows = listRecs.map((r) => r.s);
  const exceptionCounts = new Map(
    EXCEPTIONS.map((e) => [e, inStatus.filter((r) => r.exceptions.includes(e)).length] as const),
  );
  const placed = useRef<number | null>(null);
  const openRow = (id: number) => {
    placed.current = id;
    setQueue((cur) => cur ?? listRows.map((s) => s.id));
    setOpenId(id);
  };
  useEffect(() => {
    if (openId == null || placed.current === openId || !shiftsQ.data) return;
    placed.current = openId;
    const i = listRows.findIndex((s) => s.id === openId);
    if (i < 0) return;
    if (Math.floor(i / PAGE) !== page)
      edit((n) => (i >= PAGE ? n.set('side', String(Math.floor(i / PAGE) + 1)) : n.delete('side')));
    requestAnimationFrame(() =>
      document.querySelector(`[data-review-open="${openId}"]`)?.scrollIntoView?.({ block: 'nearest' }),
    );
  });
  const askedFix = sp.get('rett');
  const [startEdit, setStartEdit] = useState<'in' | 'out' | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when the period has loaded
  useEffect(() => {
    if (!askedFix || !shiftsQ.data) return;
    if (openId != null && mayEdit && shifts.some((s) => s.id === openId))
      setStartEdit(askedFix === 'ut' ? 'out' : 'in');
    edit((n) => n.delete('rett'));
  }, [askedFix, shiftsQ.data]);

  // ── Per ansatt ───────────────────────────────────────────────────────────────────────────
  const peopleAll = perPerson(scopedRecs);
  const pFilter: PeopleFilter = (['gjenstar', 'ferdig'] as PeopleFilter[]).includes(
    sp.get('astatus') as PeopleFilter,
  )
    ? (sp.get('astatus') as PeopleFilter)
    : 'alle';
  const pSort = sp.get('asort') === 'navn' ? 'navn' : 'prioritet';
  const peopleRows = sortPeople(
    peopleAll.filter((p) =>
      pFilter === 'ferdig' ? p.complete : pFilter === 'gjenstar' ? !p.complete : true,
    ),
    pSort,
    nameById,
  );
  /** a count, «Åpne» or the Ansatt dropdown on Per ansatt: Registreringer for that person and status (D156) */
  const drill = (id: number, d: Drill) =>
    edit((n) => {
      n.set('fane', 'registreringer');
      n.set('ansatt', String(id));
      n.set('fra', 'ansatte');
      for (const k of ['side', 'vakt', 'avvik']) n.delete(k);
      n.set('vis', d);
    }, true);
  const backToPeople = () =>
    edit((n) => {
      n.set('fane', 'ansatte');
      for (const k of ['ansatt', 'fra', 'vis', 'avvik', 'side', 'vakt', 'aside', 'sort']) n.delete(k);
    }, true);
  const drilled = sp.get('fra') === 'ansatte' && who !== 'alle';

  // ── the record panel ─────────────────────────────────────────────────────────────────────
  const openShift = openId == null ? null : (shifts.find((s) => s.id === openId) ?? null);
  const drawerShift = drawer == null ? null : (shifts.find((s) => s.id === drawer) ?? null);
  const drawerPerson = drawerShift ? personOf.get(drawerShift.user_id ?? -1) : undefined;
  const historyQ = useWorkTimeChanges({ shift_id: openShift?.id ?? undefined }, openShift != null);
  const captured = queue ?? listRows.map((s) => s.id);
  const eligible = (id: number) => {
    const r = recs.get(id);
    return !!r && (r.bucket === 'review' || r.bucket === 'ready') && !r.exceptions.includes('waiting');
  };
  const closePanel = () =>
    askBeforeLeaving(T('Lukk registreringen'), () => {
      setQueue(null);
      setStartEdit(null);
      setOpenId(null);
    });
  const goTo = (id: number) =>
    askBeforeLeaving(T('Neste registrering'), () => {
      placed.current = id;
      setStartEdit(null);
      setOpenId(id);
    });
  async function approveOne(s: ApiShift, next: boolean, reason?: string): Promise<boolean> {
    const ok = await approve([s.id], reason);
    if (!ok) return false;
    if (!next) {
      toast(T('Registreringen er godkjent og med i lønnsgrunnlaget.'));
      return true;
    }
    const n = nextIn(captured, s.id, (id) => id !== s.id && eligible(id));
    if (n == null) {
      toast(T('Godkjent. Ingen flere registreringer å behandle i denne køen.'));
      setQueue(null);
      setOpenId(null);
    } else {
      toast(T('Godkjent. Neste registrering er åpnet.'));
      placed.current = n;
      setOpenId(n);
    }
    return true;
  }

  const mayAskFor = (s: ApiShift) =>
    (mayEdit || mayApprove) && s.user_id !== me && s.user_id != null && !s.is_approved;
  const detail = (s: ApiShift) => {
    const r = recOf(s);
    const person = personOf.get(s.user_id ?? -1);
    const ended = r.bucket !== 'planned' && r.bucket !== 'ongoing';
    const entries = person && ended ? dayEntries(s, rulePerson(person, tenant), rules) : [];
    // a deviation's case says how far off the punch is; its rule line would say it again (#272 K-10)
    const shown = (
      r.exceptions.includes('deviation')
        ? entries.filter((x) => !/^punch_(in|out)_(late|early)$/.test(String(x.code)))
        : entries
    ).map((x) => ruleText(t, x));
    const c = clarOf(s.id);
    return (
      <KontrollRecord
        key={s.id}
        r={r}
        queue={captured}
        onGo={goTo}
        onClose={closePanel}
        policy={policy}
        name={nameOf(s)}
        sub={subOf(s.user_id)}
        clar={c}
        history={
          historyQ.isPending
            ? null
            : historyQ.isError
              ? undefined
              : Array.isArray(historyQ.data)
                ? historyQ.data
                : []
        }
        entries={[...new Set(shown)]}
        writable={writable}
        mayEdit={mayEdit}
        mayApprove={mayApprove}
        mayAsk={mayAskFor(s)}
        busy={busy}
        startEdit={startEdit}
        onApprove={(next, reason) => approveOne(s, next, reason)}
        onReopen={async (reason) => {
          // the same door as approving, false: bound to the values shown, refused in a closed pay month (D124)
          try {
            await put(`/admin/shifts/${s.id}/accept`, {
              is_approved: false,
              if_unchanged: revisionOf(s),
              reason,
            });
          } finally {
            await invalidateShifts(qc);
          }
          toast(T('Godkjenningen er trukket tilbake. Registreringen er ikke med i lønnsgrunnlaget.'));
        }}
        onCorrect={async (body) => {
          await w.update.mutateAsync({ id: s.id, body });
          toast(T('Rettingen er lagret. Registreringen venter på godkjenning.'));
        }}
        onAsk={async (question) => {
          const x = await clarW.ask.mutateAsync({ shiftId: s.id, question });
          toast(
            x.delivery === 'failed'
              ? T('Avklaringen er registrert, men ikke levert. Prøv å sende på nytt.')
              : T('Avklaring registrert. Registreringen venter på ansatt.'),
          );
        }}
        onReply={async (reply) => {
          if (!c) return;
          await clarW.reply.mutateAsync({ id: c.id, reply });
          toast(T('Svaret er lagret. Vurder arbeidstiden før godkjenning.'));
        }}
        onWithdraw={() =>
          c &&
          clarW.withdraw.mutateAsync({ id: c.id, status: c.status }).then(
            () => toast(T('Avklaringen er avsluttet.')),
            (e) => toast(errorText(e)),
          )
        }
        onResend={() =>
          c &&
          clarW.resend.mutateAsync(c.id).then(
            () => toast(T('Spørsmålet er sendt på nytt.')),
            (e) => toast(errorText(e)),
          )
        }
        menu={[
          ...(can('fravaer.registrere') && s.user_id != null
            ? [
                {
                  key: 'abs',
                  label: T('Registrer fravær i stedet'),
                  onSelect: () => setAbsDraft(newAbsenceDraft(s.user_id as number, dateOf(s))),
                },
              ]
            : []),
          { key: 'open', label: T('Åpne vakten'), onSelect: () => setDrawer(s.id) },
        ]}
        extraFacts={
          projectsOn ? (
            <div>
              <dt>{projectWord.one}</dt>
              <dd>{projectName(s)}</dd>
            </div>
          ) : null
        }
      />
    );
  };

  const scopeQuery = (() => {
    const n = new URLSearchParams();
    for (const k of ['mnd', 'uke', 'team', 'ansatt', 'sok', 'prosjekt']) {
      const v = sp.get(k);
      if (v) n.set(k, v);
    }
    return n.toString();
  })();
  const batchShifts = batch ? scoped.filter((s) => batch.includes(s.id)) : null;
  const active = [
    span === 'uke' ? weekText(from) : '',
    q ? T('Søk: «{q}»', { q: needle.trim() }) : '',
    prosjekt ? projectWord.one : '',
  ].filter(Boolean);
  const listEmpty = () => {
    const box = (
      icon: 'calendar' | 'search' | 'check' | 'clock',
      title: string,
      text: string,
      actions: React.ReactNode,
    ) => (
      <section className="kempty">
        <Icon name={icon} size={28} />
        <h2>{title}</h2>
        <p>{text}</p>
        <div>{actions}</div>
      </section>
    );
    if (exception && inStatus.length)
      return box(
        'search',
        T('Ingen treff for dette avviket'),
        T('Velg en annen avvikstype eller vis hele statusgruppen.'),
        <Button tone="pri" onClick={() => edit((n) => n.delete('avvik'))}>
          {T('Vis alle avvik')}
        </Button>,
      );
    // before the first working day nothing is missing yet (#294 ONB-12): wait, do not accuse
    const start = workspace.start_date;
    if (start && start > today() && !scopedRecs.some((r) => r.bucket !== 'planned'))
      return <Waiting start={start} />;
    if (!shifts.length)
      return box(
        'calendar',
        T('Ingen registreringer i {m}', { m: span === 'uke' ? weekText(from) : monthLabel(month) }),
        T(
          'Den nye måneden har ingen registrerte timer ennå. Velg en annen måned, eller legg til en registrering.',
        ),
        <>
          <Button tone="pri" onClick={() => edit((n) => n.set('mnd', stepMonth(month, -1)), true)}>
            {T('Vis {m}', { m: monthLabel(stepMonth(month, -1)).split(' ')[0] ?? '' })}
          </Button>
          <Button disabled={!mayEdit || !writable} onClick={() => setAdding(true)}>
            {T('Legg til registrering')}
          </Button>
        </>,
      );
    if (!scopedRecs.filter((r) => r.bucket !== 'planned').length)
      return box(
        'search',
        T('Ingen registreringer i dette utvalget'),
        T('{scope}. Fjern søket eller endre person- og teamfiltrene.', { scope: scopeText }),
        <>
          <Button
            tone="pri"
            onClick={() =>
              edit((n) => {
                for (const k of ['ansatt', 'sok', 'avvik', 'fra']) n.delete(k);
              })
            }
          >
            {T('Nullstill person og søk')}
          </Button>
          <Button
            onClick={() =>
              edit((n) => {
                for (const k of ['team', 'ansatt', 'sok', 'avvik']) n.delete(k);
              })
            }
          >
            {T('Vis alle team')}
          </Button>
        </>,
      );
    // owner 2026-11-01: an empty Klare list is one short line with the way to Trenger vurdering
    if (filter === 'klare')
      return (
        <p className="kemptyline">
          {T('Ingen er klare ennå. Vurder registreringene i Trenger vurdering først.')}{' '}
          <button type="button" className="mlink" onClick={() => setFilter('handling')}>
            {T('Gå til Trenger vurdering')}
          </button>
        </p>
      );
    const n = scopedRecs.filter((r) => inFilter('alle', r)).length;
    const gaps = scopedRecs.filter((r) => r.bucket === 'gap').length;
    // only planned shifts that ended with no registration: say so, never «nothing to do» (AC-13, AC-18)
    if (n === gaps && gaps && filter !== 'mangler')
      return box(
        'clock',
        T('Ingen registreringer å vurdere'),
        T(
          '{n, plural, one {# planlagt vakt er avsluttet uten registrering.} other {# planlagte vakter er avsluttet uten registrering.}}',
          { n: gaps },
        ),
        <Button tone="pri" onClick={() => setFilter('mangler')}>
          {T('Vis vaktene')}
        </Button>,
      );
    return box(
      'check',
      filter === 'handling'
        ? T('Ingen registreringer trenger vurdering')
        : T('Ingen registreringer med valgt status'),
      T('Utvalget inneholder {r}. Du kan finne dem under andre statuser.', { r: regs(n) }),
      <>
        <Button tone="pri" onClick={() => setFilter('alle')}>
          {T('Vis alle registreringer')}
        </Button>
        {can('timerloenn.se') && (
          <Link className="btn" to={`/lonn?mnd=${month}`}>
            {T('Åpne lønnsgrunnlag')}
          </Link>
        )}
      </>,
    );
  };

  const showBand = !failed && (loading || scopedRecs.some((r) => r.bucket !== 'planned'));
  const readOnly = !mayEdit && !mayApprove;
  const pickTeam = (v: string) => {
    edit((n) => {
      v === 'alle' ? n.delete('team') : n.set('team', v);
      // a team change resets the person (D032)
      for (const k of ['ansatt', 'fra', 'side', 'aside']) n.delete(k);
    });
    clearPicks();
  };

  return (
    <div className={`timer kontroll${more ? ' kmore-open' : ''}`}>
      {absenceDialog}
      <BatchDialog
        shifts={batchShifts}
        scope={scopeText}
        hoursOf={hoursOf}
        nameOf={nameOf}
        busy={busy}
        onCancel={() => setBatch(null)}
        onConfirm={async () => {
          const ids = batch ?? [];
          setBatch(null);
          if (await approve(ids)) {
            clearPicks();
            toast(T('{n} registreringer er godkjent. Lønnsgrunnlaget er oppdatert.', { n: ids.length }));
          }
        }}
      />
      <ReportDialog
        shifts={report}
        left={inScope.filter((s) => !s.is_approved && s.punch_in_time).length}
        scope={scopeText}
        period={span === 'uke' ? weekText(from) : monthLabel(month)}
        hoursOf={hoursOf}
        nameOf={nameOf}
        busy={receipt.isPending}
        onCancel={() => setReport(null)}
        onConfirm={() => report && makeReport(report)}
      />
      <AddDialog
        open={adding}
        people={(usersQ.data ?? [])
          .filter((u) => u.active_status)
          .sort((a, b) => fullName(a).localeCompare(fullName(b), 'nb'))}
        person={who === 'alle' ? null : who}
        month={month}
        onClose={() => setAdding(false)}
        onSave={async (v) => {
          await w.create.mutateAsync({
            user_id: v.user_id,
            shift_type_id: unplannedType?.id ?? null,
            start_time: v.punch_in_time,
            end_time: v.punch_out_time,
            punch_in_time: v.punch_in_time,
            punch_out_time: v.punch_out_time,
            published: true,
            change_reason: v.change_reason,
          });
          setAdding(false);
          edit((n) => {
            n.set('fane', 'registreringer');
            n.set('ansatt', String(v.user_id));
            n.set('vis', 'klare');
            for (const k of ['team', 'sok', 'avvik', 'side', 'fra']) n.delete(k);
          }, true);
          toast(T('Registreringen er lagret og venter på godkjenning.'));
        }}
      />

      {fromSaker ? (
        // the worklist Saker sent (#286 S09): its origin, its exact records and the way back to the same task
        <div className="ksaker" data-testid="kontroll-saker">
          <div>
            <strong>{T('Arbeidsliste fra Saker')}</strong>
            <p>
              {T('{n} bestemte registreringer, {k} gjenstår.', {
                n: poster.length,
                k: inPoster.filter((s) => !s.is_approved).length,
              })}{' '}
              {inPoster.length < poster.length
                ? T('{m} av dem ligger utenfor perioden som vises.', { m: poster.length - inPoster.length })
                : ''}
            </p>
          </div>
          {safeReturn(sp.get('tilbake')) && (
            <Link
              className="btn"
              to={safeReturn(sp.get('tilbake')) as string}
              data-testid="kontroll-saker-back"
            >
              <Icon name="chevronLeft" /> {T('Tilbake til saken')}
            </Link>
          )}
        </div>
      ) : (
        sp.get('fra') !== 'lonn' &&
        safeReturn(sp.get('tilbake')) && (
          <Link className="tlback mlink" to={safeReturn(sp.get('tilbake')) as string}>
            <Icon name="chevronLeft" /> {T('Tilbake')}
          </Link>
        )
      )}
      {fromSaker && inPoster.length === poster.length && inPoster.every((s) => s.is_approved) && (
        <Notice tone="ok" title={T('Utvalget er ferdig.')}>
          {T(
            'Alle {n} registreringene er godkjent. Andre registreringer er ikke endret. Godkjenning er ikke eksport til lønn.',
            {
              n: poster.length,
            },
          )}
        </Notice>
      )}
      {sp.get('fra') === 'lonn' && (
        // the exact Lønnsgrunnlag address it came from (#264): its month, open checks and list filters
        <Link className="tlback mlink" to={payrollBack ?? `/lonn?mnd=${month}`}>
          <Icon name="chevronLeft" />{' '}
          {T('Tilbake til lønnsgrunnlaget, {m}', { m: monthLabel(payrollBackMonth ?? month) })}
        </Link>
      )}
      <TimerHead
        tab={view}
        scope={scopeQuery}
        onAdd={() => setAdding(true)}
        addDisabled={!writable || !mayEdit}
        payroll={month}
        reports={[
          { key: 'csv', label: T('Godkjente timer (CSV)'), onSelect: exportCsv, disabled: !shiftsQ.data },
          {
            key: 'record',
            label: T('Arbeidstid §10-7 (CSV)'),
            onSelect: exportRecord,
            disabled: !shiftsQ.data,
          },
        ]}
      />
      <TabPanel tab={view}>
        <div className="kscopebox">
          <div className="mbar kscope" role="toolbar" aria-label={T('Utvalg')}>
            <div className="mf kper kprim">
              <span className="tllbl">{T('Måned')}</span>
              <PeriodControl
                label={T('Måned')}
                period="month"
                month
                value={month}
                onChange={(v) => {
                  edit((n) => {
                    n.set('mnd', v);
                    n.delete('uke');
                  }, true);
                  clearPicks();
                }}
                prevLabel={T('Forrige måned')}
                nextLabel={T('Neste måned')}
                onPrev={() => {
                  edit((n) => {
                    n.set('mnd', stepMonth(month, -1));
                    n.delete('uke');
                  }, true);
                  clearPicks();
                }}
                onNext={() => {
                  edit((n) => {
                    n.set('mnd', stepMonth(month, 1));
                    n.delete('uke');
                  }, true);
                  clearPicks();
                }}
              >
                <span>{monthLabel(month)}</span>
              </PeriodControl>
            </div>
            <span className="sp" />
            <label className="mf kprim kteam">
              {T('Team')}
              <select className="inp" value={team} onChange={(e) => pickTeam(e.target.value)}>
                <option value="alle">{T('Alle team')}</option>
                {allTeams.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label className="mf kprim kwho">
              {T('Ansatt')}
              <select
                className="inp"
                value={scope.length > 1 ? 'utvalg' : who}
                onChange={(e) => {
                  const v = e.target.value;
                  // on Per ansatt, or drilled from it, the dropdown is the same door as «Åpne» and «Tilbake til ansatte»
                  if (v === 'utvalg') return;
                  if (v !== 'alle' && (view === 'ansatte' || drilled)) drill(Number(v), 'alle');
                  else if (v === 'alle' && drilled) backToPeople();
                  else
                    edit((n) => {
                      v === 'alle' ? n.delete('ansatt') : n.set('ansatt', v);
                      for (const k of ['side', 'aside', 'fra']) n.delete(k);
                    });
                  clearPicks();
                }}
              >
                <option value="alle">{T('Alle ansatte')}</option>
                {scope.length > 1 && (
                  <option value="utvalg">{T('{n} valgte ansatte', { n: scope.length })}</option>
                )}
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn kfilters"
              aria-expanded={more}
              aria-controls="timer-more-filters"
              onClick={() => setMore(!more)}
            >
              <Icon name="sliders" size={19} />
              <span className="kfsum">{scopeText}</span>
              <strong>
                {more ? T('Lukk filtre') : T('Flere filtre')}
                {active.length > 0 && <span className="kfcount">{active.length}</span>}
              </strong>
            </button>
          </div>
          {active.length > 0 && !more && <p className="kactive">{active.join(', ')}</p>}
          {more && (
            <div className="kmore" id="timer-more-filters">
              <label className="mf">
                {T('Periode')}
                <select
                  className="inp"
                  value={span}
                  onChange={(e) => {
                    const week = e.target.value === 'uke';
                    edit((n) => {
                      if (week) n.set('uke', weeksOf(month)[0] as string);
                      else n.delete('uke');
                      n.set('mnd', month);
                    });
                    clearPicks();
                  }}
                >
                  <option value="maned">{T('Måned')}</option>
                  <option value="uke">{T('Uke')}</option>
                </select>
              </label>
              {span === 'uke' && (
                <label className="mf">
                  {T('Uke')}
                  <select
                    className="inp"
                    value={from}
                    onChange={(e) => {
                      edit((n) => {
                        n.set('uke', e.target.value);
                        n.set('mnd', month);
                      }, true);
                      clearPicks();
                    }}
                  >
                    {weeksOf(month).map((m) => (
                      <option key={m} value={m}>
                        {weekText(m)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {projectsOn && (
                <label className="mf">
                  {projectWord.one}
                  <select
                    className="inp"
                    data-testid="timer-project"
                    value={prosjekt ?? 'alle'}
                    onChange={(e) => {
                      const v = e.target.value;
                      edit((n) => (v === 'alle' ? n.delete('prosjekt') : n.set('prosjekt', v)));
                      clearPicks();
                    }}
                  >
                    <option value="alle">{T('Alle')}</option>
                    {projects.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {projectLabel(p)}
                      </option>
                    ))}
                    <option value="uten">{projectWord.none}</option>
                  </select>
                </label>
              )}
              <label className="mf ksearch">
                {T('Søk i ansatte')}
                <span className="ksearchbox">
                  <Icon name="search" size={17} />
                  <input
                    className="inp"
                    type="search"
                    placeholder={T('Navn eller ansattnummer')}
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    onBlur={() =>
                      typed !== needle &&
                      edit((n) => {
                        typed ? n.set('sok', typed) : n.delete('sok');
                        n.delete('side');
                        n.delete('aside');
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      edit((n) => {
                        typed ? n.set('sok', typed) : n.delete('sok');
                        n.delete('side');
                        n.delete('aside');
                      });
                    }}
                  />
                </span>
              </label>
            </div>
          )}
        </div>
        {scope.length > 1 && (
          <p className="tlscope" data-testid="timer-scope">
            {T('Utvalg: {n} ansatte', { n: scope.length })}{' '}
            <button
              type="button"
              className="mlink"
              onClick={() => {
                edit((n) => n.delete('ansatt'));
                clearPicks();
              }}
            >
              {T('Fjern utvalg')}
            </button>
          </p>
        )}

        {badMonth && (
          <Notice tone="info" title={T('Adressen viser ingen gyldig måned')}>
            {T('Viser {m} i stedet.', { m: monthLabel(month) })}
          </Notice>
        )}
        {readOnly && (
          <Notice tone="info" title={T('Du har lesetilgang')}>
            {T('Du kan se registreringer og grunnlag. Retting og godkjenning krever ledertilgang.')}
          </Notice>
        )}
        {failed && (
          <section className="kempty kerror" role="alert">
            <Icon name="warn" size={28} />
            <h2>{T('Kunne ikke hente registreringene')}</h2>
            <p>{T('Antall og arbeidstid kan ikke bekreftes. Prøv igjen med samme periode og utvalg.')}</p>
            <div>
              <Button tone="pri" onClick={() => shiftsQ.refetch()}>
                {T('Prøv igjen')}
              </Button>
            </div>
          </section>
        )}
        {stale && (
          <Notice
            tone="info"
            title={T('Viser sist hentede registreringer')}
            action={
              <Button small onClick={() => shiftsQ.refetch()}>
                {T('Hent siste versjon')}
              </Button>
            }
          >
            {T('Opplysningene må oppdateres før du retter eller godkjenner. Sist hentet kl. {time}.', {
              time: new Date(shiftsQ.dataUpdatedAt).toLocaleTimeString('nb-NO', {
                hour: '2-digit',
                minute: '2-digit',
              }),
            })}
          </Notice>
        )}
        {clarQ.isError && (
          <Notice
            tone="info"
            title={T('Kunne ikke hente avklaringene')}
            action={
              <Button small onClick={() => clarQ.refetch()}>
                {T('Prøv igjen')}
              </Button>
            }
          >
            {T(
              'Registreringer som venter på svar fra ansatte vises kanskje som klare. Serveren stopper likevel godkjenningen.',
            )}
          </Notice>
        )}
        {openId != null && shiftsQ.isSuccess && !openShift && (
          <Notice
            tone="info"
            title={T('Fant ikke registreringen i denne perioden')}
            action={
              <Button small onClick={() => setOpenId(null)}>
                {T('Lukk')}
              </Button>
            }
          >
            {T('Den kan være slettet, eller ligge i en annen måned. Listen viser hele utvalget.')}
          </Notice>
        )}

        {showBand && !loading && (
          <KontrollSummary
            sum={sum}
            selected={view === 'ansatte' ? null : filter}
            allSelected={view !== 'ansatte' && filter === 'alle'}
            onPick={(k) => setFilter(k)}
            onRemaining={() => setFilter('gjenstar')}
            onAll={() => setFilter('alle')}
            onOngoing={() => setFilter('pagar')}
            onGaps={() => setFilter('mangler')}
            allCount={scopedRecs.filter((r) => inFilter('alle', r)).length}
            gapsSelected={view !== 'ansatte' && filter === 'mangler'}
          />
        )}
        {loading && !failed && (
          <section className="kloading" aria-busy="true">
            <h2>{T('Henter registreringer…')}</h2>
            <p>{T('Perioden og filtrene dine beholdes.')}</p>
            <Skeleton rows={5} />
          </section>
        )}

        {view === 'ansatte'
          ? !failed &&
            !loading && (
              <KontrollPeople
                rows={peopleRows}
                all={peopleAll}
                filter={pFilter}
                sort={pSort}
                page={pageOf(sp.get('aside'))}
                nameOf={nameById}
                subOf={subOf}
                onFilter={(f) =>
                  edit((n) => {
                    f === 'alle' ? n.delete('astatus') : n.set('astatus', f);
                    n.delete('aside');
                  })
                }
                onSort={(s) => edit((n) => (s === 'prioritet' ? n.delete('asort') : n.set('asort', s)))}
                onPage={(p) => edit((n) => (p > 0 ? n.set('aside', String(p + 1)) : n.delete('aside')), true)}
                onDrill={drill}
                worktime={`/arbeidstid?vis=oversikt&${span === 'uke' ? `periode=uke&dato=${from}` : `periode=maned&mnd=${month}`}`}
              />
            )
          : !failed &&
            !loading && (
              <>
                {worklist && (
                  <div className="mnote info" aria-live="polite">
                    <div className="mnt">
                      <b>{T(WORKLISTS[worklist])}</b>
                      <span>
                        {T('Arbeidsliste fra Oversikt')}, {regs(listRows.length)}
                      </span>
                    </div>
                    <Button
                      onClick={() => {
                        edit((n) => n.delete('arbeidsliste'));
                        clearPicks();
                      }}
                    >
                      {T('Vis hele perioden')}
                    </Button>
                  </div>
                )}
                {drilled && (
                  <button type="button" className="mlink kback" onClick={backToPeople}>
                    <Icon name="chevronLeft" size={19} />
                    {T('Tilbake til ansatte')}
                  </button>
                )}
                <div className={`msplit${openShift ? ' open' : ''}`}>
                  <section className="mcard tlcard" aria-labelledby="kqueue-h">
                    <div className="kqh">
                      <h2 id="kqueue-h" tabIndex={-1}>
                        {T(FILTER_TEXT[filter])} <span className="kqn">{listRows.length}</span>
                      </h2>
                      <div className="kqf">
                        <label>
                          <span className="sr">{T('Filtrer avvik')}</span>
                          <select
                            className="inp kexc"
                            value={exception ?? 'alle'}
                            data-testid="timer-exception"
                            aria-label={T('Filtrer avvik')}
                            onChange={(e) => {
                              const v = e.target.value;
                              edit((n) => {
                                v === 'alle' ? n.delete('avvik') : n.set('avvik', v);
                                if (n.get('vis') === 'avvik' || n.get('vis') === 'uten')
                                  n.set('vis', 'handling');
                                n.delete('side');
                              });
                              clearPicks();
                            }}
                          >
                            <option value="alle">{T('Alle avvik')}</option>
                            {EXCEPTIONS.map((e) => (
                              <option key={e} value={e}>
                                {T(EXCEPTION_TEXT[e])} ({exceptionCounts.get(e) ?? 0})
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span className="sr">{T('Sorter')}</span>
                          <select
                            className="inp ksort"
                            value={sort}
                            data-testid="timer-sort"
                            aria-label={T('Sorter registreringer')}
                            onChange={(e) => {
                              const v = e.target.value;
                              edit((n) => (v === 'prioritet' ? n.delete('sort') : n.set('sort', v)));
                            }}
                          >
                            <option value="prioritet">{T('Avvik først')}</option>
                            <option value="dato">{T('Dato')}</option>
                            <option value="ansatt">{T('Ansatt')}</option>
                          </select>
                        </label>
                      </div>
                    </div>
                    {filter === 'handling' && (
                      <p className="khintline">
                        {T(
                          'Når du har vurdert en registrering, flyttes den til Klare til godkjenning, der du kan godkjenne flere samtidig.',
                        )}
                      </p>
                    )}
                    {listRows.length ? (
                      <KontrollQueue
                        recs={listRecs}
                        total={listRecs.length}
                        page={page}
                        onPage={setPage}
                        policy={policy}
                        canPick={mayApprove && writable && filter === 'klare'}
                        picked={picked}
                        setPicked={setPicked}
                        openId={openId}
                        onOpen={openRow}
                        nameOf={nameOf}
                        subOf={subOf}
                        project={
                          projectsOn
                            ? { heading: projectWord.one, of: projectName, lower: projectWord.lower }
                            : undefined
                        }
                        caption={T('Registreringer for {scope}', { scope: scopeText })}
                        onBatch={(ids) => setBatch(ids)}
                        busy={busy}
                      />
                    ) : (
                      listEmpty()
                    )}
                  </section>
                  {openShift && detail(openShift)}
                </div>
              </>
            )}
      </TabPanel>
      <ShiftDrawer
        draft={drawerShift ? (draftOf(drawerShift) as DraftShift) : null}
        tasks={drawerShift?.taskAssignments}
        entries={
          drawerShift && drawerPerson ? dayEntries(drawerShift, rulePerson(drawerPerson, tenant), rules) : []
        }
        types={typesQ.data ?? []}
        users={(usersQ.data ?? []).filter((u) => u.active_status)}
        onClose={() => setDrawer(null)}
        onDelete={(id, reason) =>
          w.remove.mutateAsync({ id, reason }).then(
            () => {
              toast(T('Vakten er slettet'));
              setDrawer(null);
              setOpenId(null);
            },
            (e) => toast(errorText(e)),
          )
        }
      />
      <AbsenceDrawer draft={absDraft} shifts={shifts} onClose={() => setAbsDraft(null)} />
    </div>
  );
}
